import { BRAND } from '@verlo/shared/lib/brand';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { brandingApi } from '@verlo/shared/services/api';

/**
 * Single-org branding context (P6-FE-SWITCHER).
 *
 * The tenant app now serves exactly one org — the one its subdomain resolves to
 * (P6-HOST-RESOLVE) — so there is nothing to switch and no super-user cross-org view here; org
 * management moved out to the super-admin control plane. This is what the S6-2 switcher and its
 * active-org machinery collapsed to: a read-only branding provider for the back-office chrome.
 *
 * Branding comes from the public `GET /branding` (P6-PUBLIC-BRANDING), which host-resolves the
 * org server-side and needs no auth — the same source the login page can theme from pre-auth,
 * rather than the authenticated org list the switcher read. It degrades to default Verlo branding
 * on any failure (offline, node-ui which mounts no such endpoint, or a host that resolves to no
 * live org), so it is safe to mount without gating.
 */
const OrgContext = createContext(null);

/** Verlo's own chrome, and the fallback for every unset branding field. */
export const DEFAULT_BRANDING = {
  display_name: '',
  logo_url: '',
  primary_color: BRAND.verlo,
  accent_color: '#0066B3',
};

export const useOrg = () => {
  const ctx = useContext(OrgContext);
  if (!ctx) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return ctx;
};

export const OrgProvider = ({ children }) => {
  const [raw, setRaw] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    brandingApi.get()
      .then((res) => { if (alive) setRaw(res.data || null); })
      .catch(() => { if (alive) setRaw(null); })   // node-ui / offline / no live org → default
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  // The endpoint already merges its own defaults server-side, but a field it leaves null (an
  // unbranded or unknown host) still falls back to the Verlo chrome colour here.
  const branding = useMemo(() => {
    const b = raw || {};
    return {
      display_name: b.display_name || DEFAULT_BRANDING.display_name,
      logo_url: b.logo_url || DEFAULT_BRANDING.logo_url,
      primary_color: b.primary_color || DEFAULT_BRANDING.primary_color,
      accent_color: b.accent_color || DEFAULT_BRANDING.accent_color,
    };
  }, [raw]);

  return <OrgContext.Provider value={{ branding, loaded }}>{children}</OrgContext.Provider>;
};

export default OrgContext;
